import { FC, useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ChartEmptyState } from '../../../leopart-update/components/AthleteInjuryCharts/charts/_shared/ChartEmptyState';
import {
  chartAxisTick,
  chartGridProps,
  chartLegendStyle,
  chartTooltipContentStyle,
  chartTooltipItemStyle,
  chartTooltipLabelStyle,
} from '../../../shared/charts/chartTheme';
import { ITrainingMixSeriesPoint, TrainingMixGranularity } from '../../../models/allenamenti';
import { FALLBACK_CATEGORY_COLOR } from '../allenamenti.constants';
import { formatBucket, formatMinutes } from './analysis.constants';
import Typhography from '../../../shared/Typhography/Typhography';
import styles from './TrendChart.module.scss';

interface ITrendChartProps {
  series: ITrainingMixSeriesPoint[];
  granularity: TrainingMixGranularity;
  bySlug: Map<string, { labelIt: string; color: string }>;
  /** Altezza del grafico, standard 260 come EffortLoadChart. */
  height?: number;
}

/**
 * Trend impilato per categoria nel tempo — l'unico chart del repo con
 * `stackId` (nessun precedente da copiare in tutto apps/frontend/src, vedi
 * design M2/M3 §5.3). Assi/griglia/tooltip riusano gli stessi token di
 * EffortLoadChart via shared/charts/chartTheme.
 *
 * Le serie sono dinamiche: le categorie sono dati del tenant, non un enum,
 * quindi si scoprono dai bucket ricevuti invece di essere elencate a mano —
 * altrimenti una categoria custom del tenant sparirebbe silenziosamente dal
 * grafico.
 */
const TrendChart: FC<ITrendChartProps> = ({ series, granularity, bySlug, height = 260 }) => {
  const categoryKeys = useMemo(() => {
    const totals = new Map<string, number>();
    for (const point of series) {
      for (const [key, minutes] of Object.entries(point.byCategory)) {
        totals.set(key, (totals.get(key) ?? 0) + minutes);
      }
    }
    // Le categorie con più minuti fanno da base della pila — solo
    // leggibilità, nessun ordine di dominio da rispettare.
    return [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([key]) => key);
  }, [series]);

  const chartData = useMemo(
    () => series.map((point) => ({ bucket: point.bucket, ...point.byCategory })),
    [series],
  );

  if (series.length === 0 || categoryKeys.length === 0) {
    return (
      <div className={styles.card}>
        <Typhography type="h3" size="bold-14" className={styles.title}>Andamento nel tempo</Typhography>
        <ChartEmptyState />
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <Typhography type="h3" size="bold-14" className={styles.title}>Andamento nel tempo</Typhography>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid {...chartGridProps} />
          <XAxis
            dataKey="bucket"
            tickFormatter={(v) => formatBucket(String(v), granularity)}
            tick={chartAxisTick}
          />
          <YAxis
            tick={chartAxisTick}
            width={56}
            allowDecimals={false}
            tickFormatter={(v) => formatMinutes(Number(v))}
          />
          <Tooltip
            contentStyle={chartTooltipContentStyle}
            labelStyle={chartTooltipLabelStyle}
            itemStyle={chartTooltipItemStyle}
            labelFormatter={(label) => formatBucket(String(label), granularity)}
            formatter={(value, name) => [
              formatMinutes(Number(value)),
              bySlug.get(String(name))?.labelIt ?? String(name),
            ]}
          />
          <Legend
            formatter={(value) => bySlug.get(String(value))?.labelIt ?? String(value)}
            wrapperStyle={chartLegendStyle}
          />
          {categoryKeys.map((key) => (
            <Bar
              key={key}
              dataKey={key}
              stackId="mix"
              fill={bySlug.get(key)?.color ?? FALLBACK_CATEGORY_COLOR}
              maxBarSize={44}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TrendChart;
